'use client';

import { API_ROUTES } from '@shared/api-utils';
import { Button, FormField } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { NumericCode } from '@shared/icons/system';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMultistepForm } from '../../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { OtpInput } from '../../../../../../form-controls/OtpInput/OtpInput';
import { toast } from '../../../../../../Toast/Toast';
import { useVerificationApiMutation } from '../../../../hooks/useVerificationApiMutation';
import { invalidateMyAccountVerificationQueries } from '../../../../utils/invalidateMyAccountVerificationQueries';
import { getEmailOtpResendLimiterStorageKey } from '../../../../utils/otpResendLimiterStorageKey';
import { isVerificationRateLimitError, resolveVerificationMutationFailure } from '../../../../utils/verificationErrorUtils';
import { SettingsVerificationTechnicalFailure } from '../../../SettingsVerificationTechnicalFailure';
import type { ResendVerificationPayload, ResendVerificationResponse, VerifyVerificationPayload, VerifyVerificationResponse } from '../../types';

const EMAIL_VERIFICATION_CODE_PATTERN = /^\d{6}$/;

type VerifyStepProps = {
  onVerificationFailureDone?: () => void;
};

const VerifyStep = ({ onVerificationFailureDone }: VerifyStepProps = {}) => {
  const t = useTranslations('my_account');
  const queryClient = useQueryClient();
  const form = useFormContext<{ otp: string; email: string; verificationId: string }>();
  const otp = useWatch({ control: form.control, name: 'otp' }) ?? '';
  const email = useWatch({ control: form.control, name: 'email' }) ?? '';
  const isOtpComplete = EMAIL_VERIFICATION_CODE_PATTERN.test(otp);
  const otpResendStorageKey = getEmailOtpResendLimiterStorageKey(email);
  const { goToNextStep } = useMultistepForm();
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  const [showTechnicalFailure, setShowTechnicalFailure] = useState(false);
  const isVerifyingRef = useRef(false);

  const { mutateAsync: verifyCode, isPending: isVerifying } = useVerificationApiMutation<VerifyVerificationResponse, VerifyVerificationPayload>(
    API_ROUTES.MY_ACCOUNT.VERIFICATION.VERIFY
  );

  const { mutateAsync: resendCode } = useVerificationApiMutation<ResendVerificationResponse, ResendVerificationPayload>(API_ROUTES.MY_ACCOUNT.VERIFICATION.RESEND);

  const handleVerify = async () => {
    if (isVerifyingRef.current || isVerifying) return;

    isVerifyingRef.current = true;

    try {
      const isValid = await form.trigger('otp');
      if (!isValid) return;

      await verifyCode({ verificationId: form.getValues('verificationId'), code: form.getValues('otp') });
      invalidateMyAccountVerificationQueries(queryClient);
      goToNextStep();
    } catch (error) {
      resolveVerificationMutationFailure(error, {
        onRateLimit: () => setMaxAttemptsReached(true),
        onTechnical: () => setShowTechnicalFailure(true),
        onBusiness: () => {
          const message = t('pages.edit_email.verify_step.error');

          if (getSiteConfig().MY_ACCOUNT_SETTINGS.showsWrongOtpAsToast) {
            toast(message, { type: 'error' });
          } else {
            form.setError('otp', { message });
          }
        },
      });
    } finally {
      isVerifyingRef.current = false;
    }
  };

  const handleResend = async () => {
    try {
      await resendCode({ verificationId: form.getValues('verificationId') });
    } catch (error) {
      if (isVerificationRateLimitError(error)) {
        setMaxAttemptsReached(true);
        throw error;
      }

      toast(t('pages.edit_email.verify_step.resend_error'), { type: 'error' });
      throw error;
    }
  };

  if (showTechnicalFailure) {
    return (
      <div className="flex flex-col h-full" id="verify-step" data-testid="verify-step">
        <SettingsVerificationTechnicalFailure
          action={
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={() => onVerificationFailureDone?.()}
              data-testid="verification-technical-failure-done"
              data-track-name="Done"
            >
              {t('settings.verification_technical_failure.done')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" id="verify-step" data-testid="verify-step">
      <div className="md:mt-6">
        <NumericCode size="extra-extra-large" className="mb-5" data-testid="numeric-code-icon" />
        <h4 className="mb-8 text-pretty break-words" data-testid="verify-step-title">
          {t.rich('pages.edit_email.verify_step.title', {
            address: chunks => (
              <span className="break-all" data-track-privacy="Mask">
                {chunks}
              </span>
            ),
            email,
          })}
        </h4>

        <FormField
          name="otp"
          render={({ field, fieldState }) => (
            <OtpInput
              id="otp-input"
              storageKey={otpResendStorageKey}
              startCooldownOnMount
              maxAttemptsReachedOverride={maxAttemptsReached}
              onOtpChange={field.onChange}
              onResend={handleResend}
              helpMessage={fieldState.error?.message}
              helpMessageType={fieldState.error ? 'error' : undefined}
            />
          )}
        />
      </div>

      <div data-track-container="Actions">
        <Button
          type="button"
          variant="primary"
          onClick={handleVerify}
          disabled={isVerifying || !isOtpComplete}
          className="mt-auto"
          data-testid="verify-email-step"
          data-track-name="Continue"
        >
          {t('pages.edit_email.verify_step.confirm')}
        </Button>
      </div>
    </div>
  );
};

export { VerifyStep };
